import React from 'react'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'

import { ExclamationTriangleIcon, LockClosedIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import { Button } from './ui'

interface ErrorWithStatus extends Error {
  status?: number
}

type ErrorKind = 'auth' | 'network' | 'forbidden' | 'notfound' | 'server' | 'generic'

// Raw fetch/network failure strings the browsers throw, none of which mean
// anything to a user. We catch these and speak plainly instead.
const NETWORK_ERROR_HINTS = [
  'failed to fetch',
  'networkerror',
  'network request failed',
  'load failed',
  'the internet connection appears to be offline',
]

// Turn a thrown error into human copy: a short title and one helpful sentence
// about what to do next. Never leaks the raw technical message into the title.
export function resolveError(error: ErrorWithStatus): { kind: ErrorKind; title: string; message: string } {
  const status = error?.status
  const raw = (error?.message || '').toLowerCase()
  const isNetwork = NETWORK_ERROR_HINTS.some((hint) => raw.includes(hint))

  if (status === 401) {
    return { kind: 'auth', title: 'Your session has expired', message: 'Please log in again to pick up where you left off.' }
  }
  if (isNetwork) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    return {
      kind: 'network',
      title: "We couldn't reach the server",
      message: offline
        ? 'You appear to be offline. Reconnect and try again.'
        : 'Please check your internet connection and try again.',
    }
  }
  if (status === 403) {
    return { kind: 'forbidden', title: "You don't have access to this", message: 'If you think this is a mistake, contact your administrator.' }
  }
  if (status === 404) {
    return { kind: 'notfound', title: "We couldn't find that page", message: 'It may have been moved or removed.' }
  }
  if (status && status >= 500) {
    return { kind: 'server', title: 'Something went wrong on our end', message: 'Our server ran into a problem. Please try again in a moment.' }
  }
  return { kind: 'generic', title: 'Something went wrong', message: 'An unexpected error stopped this page from loading. Please try again.' }
}

const KIND_ICON: Record<ErrorKind, typeof ExclamationTriangleIcon> = {
  auth: LockClosedIcon,
  forbidden: LockClosedIcon,
  notfound: MagnifyingGlassIcon,
  network: ExclamationTriangleIcon,
  server: ExclamationTriangleIcon,
  generic: ExclamationTriangleIcon,
}

function ErrorFallback ({ error, resetErrorBoundary }: { error: ErrorWithStatus; resetErrorBoundary: () => void }) {
  const { kind, title, message } = resolveError(error)
  const Icon = KIND_ICON[kind]
  const canRetry = kind === 'network' || kind === 'server'
  // Keep the raw message available (collapsed) only where it may carry real
  // detail; network/auth/403/404 are fully explained by the friendly copy.
  const showDetails = (kind === 'generic' || kind === 'server') && Boolean(error?.message)

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-xs">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cerise-red-50">
          <Icon className="h-6 w-6 text-cerise-red" aria-hidden="true" />
        </div>
        <h1 className="mt-5 text-lg font-semibold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{message}</p>

        {showDetails && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">Technical details</summary>
            <p className="mt-1 wrap-break-word font-mono text-xs text-gray-400">{error.message}</p>
          </details>
        )}

        <div className="mt-6 flex items-center justify-center gap-4">
          {kind === 'auth' ? (
            <Button onClick={resetErrorBoundary}>Log in</Button>
          ) : canRetry ? (
            <>
              <Button onClick={() => window.location.reload()}>Try again</Button>
              <button
                type="button"
                onClick={resetErrorBoundary}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Back to dashboard
              </button>
            </>
          ) : (
            <Button onClick={resetErrorBoundary}>Back to dashboard</Button>
          )}
        </div>
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  history?: { push: (path: string) => void }
  navigate?: (path: string) => void
}

export function ErrorBoundary ({ children, history, navigate }: ErrorBoundaryProps) {
  const [error, setError] = React.useState<ErrorWithStatus | null>(null)

  const go = (path: string) => {
    if (navigate) {
      navigate(path)
    } else if (history) {
      history.push(path)
    }
  }

  const onError = async (error: ErrorWithStatus) => {
    setError(error)
    console.error(error)
  }

  const onReset = () => {
    // resetErrorBoundary re-runs this: 401 sends the user to log in again,
    // everything else drops them back on the dashboard.
    go(error?.status === 401 ? '/app/login' : '/app/home')
  }

  return (
    <ReactErrorBoundary onError={onError} FallbackComponent={ErrorFallback} onReset={onReset}>
      {children}
    </ReactErrorBoundary>
  )
}
